// Owns the vault's in-memory lifecycle. The master DEK lives ONLY in a ref. Each
// attribute is encrypted with its own key, wrapped by the DEK, and stored in
// user_attributes; the DEK's PIN/passkey envelopes live in user_key_envelopes.

import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  AttributeDefinition,
  AttributeValue,
  KeyEnvelopeRecord,
  PrfCapability,
  SALT_BYTES,
  VaultStatus,
} from './types';
import {
  bytesToB64,
  bytesToB64url,
  b64ToBytes,
  b64urlToBytes,
  decryptData,
  deriveKeyFromPin,
  deriveKeyFromPrf,
  encryptData,
  generateDEK,
  randomBytes,
  unwrapDEK,
  wrapDEK,
} from './crypto';
import {
  fetchKeyEnvelopes,
  persistKeyEnvelopes,
  persistPasskeyEnvelope,
  persistPinEnvelope,
  removePasskeyEnvelope,
  VaultTableMissingError,
} from './supabaseAdapter';
import {
  deleteAttribute as apiDeleteAttribute,
  fetchAttributeDefinitions,
  fetchMyAttributes,
  summarizeAttributeValue,
  upsertAttribute,
} from '../attributesService';
import {
  getPrfOutput,
  isWebAuthnSupported,
  platformAuthenticatorAvailable,
  registerPasskeyWithPrf,
} from './webauthn';

const MAX_PIN_ATTEMPTS = 10;
const LOCKOUT_MS = 5 * 60 * 1000;
const attemptsKey = (userId: string) => `zkvault_attempts_${userId}`;

interface AttemptState { count: number; lockedUntil: number; }
function readAttempts(userId: string): AttemptState {
  try { const raw = localStorage.getItem(attemptsKey(userId)); if (raw) return JSON.parse(raw); } catch { /* */ }
  return { count: 0, lockedUntil: 0 };
}
function writeAttempts(userId: string, s: AttemptState) {
  try { localStorage.setItem(attemptsKey(userId), JSON.stringify(s)); } catch { /* */ }
}

export type AttributeMap = Record<string, AttributeValue>;

export interface VaultContextValue {
  status: VaultStatus;
  capability: PrfCapability;
  definitions: AttributeDefinition[];
  attributes: AttributeMap;       // decrypted; populated only when unlocked
  hasPin: boolean;
  hasPasskey: boolean;
  error: string | null;

  setupWithPin(pin: string): Promise<void>;
  unlockWithPin(pin: string): Promise<void>;
  unlockWithPasskey(): Promise<void>;
  saveAttribute(attributeId: string, value: AttributeValue): Promise<void>;
  removeAttribute(attributeId: string): Promise<void>;
  pickAttributes(ids: string[]): AttributeMap;   // subset for consented sharing
  addPasskey(): Promise<void>;
  removePasskey(): Promise<void>;
  changePin(newPin: string): Promise<void>;
  lock(): void;
  refresh(): Promise<void>;
  clearError(): void;
}

export const VaultContext = createContext<VaultContextValue | null>(null);

function lockedStatusFor(rec: KeyEnvelopeRecord | null): VaultStatus {
  if (rec && (rec.envelopePin || rec.envelopePasskey)) return 'locked';
  return 'uninitialized';
}

// --- per-attribute crypto helpers (over the in-memory DEK) ---
async function encryptAttribute(dek: CryptoKey, value: AttributeValue) {
  const kAttr = await generateDEK();
  const details = await encryptData(kAttr, JSON.stringify(value));
  const wrapped = await wrapDEK(kAttr, dek);
  return { encrypted_details: JSON.stringify(details), encrypted_symmetric_key: JSON.stringify(wrapped) };
}
async function decryptAttribute(dek: CryptoKey, encryptedDetails: string, encryptedSymKey: string): Promise<AttributeValue> {
  const kAttr = await unwrapDEK(JSON.parse(encryptedSymKey), dek);
  const json = await decryptData(kAttr, JSON.parse(encryptedDetails));
  return JSON.parse(json) as AttributeValue;
}

export const VaultProvider: React.FC<{ session: any; children: React.ReactNode }> = ({ session, children }) => {
  const dekRef = useRef<CryptoKey | null>(null);
  const [record, setRecord] = useState<KeyEnvelopeRecord | null>(null);
  const [status, setStatus] = useState<VaultStatus>('loading');
  const [definitions, setDefinitions] = useState<AttributeDefinition[]>([]);
  const [attributes, setAttributes] = useState<AttributeMap>({});
  const [error, setError] = useState<string | null>(null);
  const [capability, setCapability] = useState<PrfCapability>({ webauthnSupported: false, platformAuthenticatorAvailable: false });

  const userId: string | undefined = session?.user?.id;

  const lock = useCallback(() => {
    dekRef.current = null;
    setAttributes({});
    setStatus((prev) => (prev === 'unlocked' ? 'locked' : prev));
  }, []);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const webauthnSupported = isWebAuthnSupported();
      const platform = webauthnSupported ? await platformAuthenticatorAvailable() : false;
      if (mounted) setCapability({ webauthnSupported, platformAuthenticatorAvailable: platform });
    })();
    return () => { mounted = false; };
  }, []);

  // Load the attribute catalog once a session exists.
  useEffect(() => {
    if (!userId) return;
    fetchAttributeDefinitions().then(setDefinitions).catch((e) => console.error('definitions load failed', e));
  }, [userId]);

  const loadRecord = useCallback(async () => {
    setError(null);
    if (!userId) {
      dekRef.current = null; setRecord(null); setAttributes({}); setStatus('loading'); return;
    }
    setStatus('loading');
    try {
      const rec = await fetchKeyEnvelopes();
      setRecord(rec);
      if (dekRef.current) setStatus('unlocked');
      else setStatus(lockedStatusFor(rec));
    } catch (e: any) {
      setError(e instanceof VaultTableMissingError ? e.message : (e?.message || 'Failed to load your vault.'));
      setStatus('error');
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) { lock(); setRecord(null); setStatus('loading'); return; }
    loadRecord();
  }, [userId, loadRecord, lock]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') { dekRef.current = null; setAttributes({}); setRecord(null); setStatus('loading'); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Auto-lock when the tab is hidden for a while.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onVis = () => {
      if (document.visibilityState === 'hidden') timer = setTimeout(() => lock(), 5 * 60 * 1000);
      else if (timer) { clearTimeout(timer); timer = null; }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); if (timer) clearTimeout(timer); };
  }, [lock]);

  const requireUserId = (): string => {
    if (!userId) throw new Error('You must be signed in to use the vault.');
    return userId;
  };

  // Decrypt all of the user's attribute rows into the in-memory map.
  const hydrateAttributes = useCallback(async (dek: CryptoKey) => {
    const rows = await fetchMyAttributes();
    const map: AttributeMap = {};
    for (const r of rows) {
      try {
        map[r.attribute_id] = await decryptAttribute(dek, r.encrypted_details, r.encrypted_symmetric_key);
      } catch (e) {
        console.error('Failed to decrypt attribute', r.attribute_id, e);
      }
    }
    setAttributes(map);
  }, []);

  const setupWithPin = useCallback(async (pin: string) => {
    requireUserId();
    setError(null);
    try {
      const dek = await generateDEK();
      const salt = randomBytes(SALT_BYTES);
      const kek = await deriveKeyFromPin(pin, salt);
      const envelopePin = await wrapDEK(dek, kek);
      const rec = await persistKeyEnvelopes({ envelopePin, pinSalt: bytesToB64(salt), envelopePasskey: null, passkeyId: null });
      dekRef.current = dek;
      setRecord(rec);
      setAttributes({});
      setStatus('unlocked');
    } catch (e: any) { setError(e?.message || 'Failed to set up the vault.'); throw e; }
  }, [userId]);

  const unlockWithPin = useCallback(async (pin: string) => {
    const uid = requireUserId();
    setError(null);
    if (!record?.envelopePin || !record.pinSalt) throw new Error('No PIN is set on this vault.');
    const attempts = readAttempts(uid);
    if (attempts.lockedUntil > Date.now()) {
      const mins = Math.ceil((attempts.lockedUntil - Date.now()) / 60000);
      const msg = `Too many attempts. Try again in ${mins} minute(s) or use a passkey.`;
      setError(msg); throw new Error(msg);
    }
    try {
      const kek = await deriveKeyFromPin(pin, b64ToBytes(record.pinSalt));
      const dek = await unwrapDEK(record.envelopePin, kek);
      dekRef.current = dek;
      await hydrateAttributes(dek);
      setStatus('unlocked');
      writeAttempts(uid, { count: 0, lockedUntil: 0 });
    } catch {
      const count = attempts.count + 1;
      const lockedUntil = count >= MAX_PIN_ATTEMPTS ? Date.now() + LOCKOUT_MS : 0;
      writeAttempts(uid, { count: lockedUntil ? 0 : count, lockedUntil });
      const msg = lockedUntil
        ? 'Too many attempts. The vault is temporarily locked. Try a passkey or wait 5 minutes.'
        : `Incorrect PIN. ${MAX_PIN_ATTEMPTS - count} attempt(s) left.`;
      setError(msg); throw new Error(msg);
    }
  }, [record, userId, hydrateAttributes]);

  const unlockWithPasskey = useCallback(async () => {
    requireUserId();
    setError(null);
    if (!record?.envelopePasskey || !record.passkeyId) throw new Error('No passkey is set on this vault.');
    try {
      const { prfOutput } = await getPrfOutput({ credentialId: b64urlToBytes(record.passkeyId) });
      const kek = await deriveKeyFromPrf(prfOutput);
      const dek = await unwrapDEK(record.envelopePasskey, kek);
      dekRef.current = dek;
      await hydrateAttributes(dek);
      setStatus('unlocked');
    } catch (e: any) { const msg = e?.message || 'Passkey unlock failed.'; setError(msg); throw new Error(msg); }
  }, [record, hydrateAttributes]);

  const saveAttribute = useCallback(async (attributeId: string, value: AttributeValue) => {
    if (!dekRef.current) throw new Error('Vault is locked.');
    setError(null);
    try {
      const { encrypted_details, encrypted_symmetric_key } = await encryptAttribute(dekRef.current, value);
      // Coarse, non-sensitive summary for privacy-preserving matching (the precise
      // value stays in encrypted_details).
      const def = definitions.find((d) => d.id === attributeId);
      const value_summary = def ? summarizeAttributeValue(def.data_type, value) : null;
      await upsertAttribute({ attribute_id: attributeId, encrypted_details, encrypted_symmetric_key, value_summary });
      setAttributes((m) => ({ ...m, [attributeId]: value }));
    } catch (e: any) { setError(e?.message || 'Failed to save.'); throw e; }
  }, [definitions]);

  const removeAttribute = useCallback(async (attributeId: string) => {
    setError(null);
    try {
      await apiDeleteAttribute(attributeId);
      setAttributes((m) => { const n = { ...m }; delete n[attributeId]; return n; });
    } catch (e: any) { setError(e?.message || 'Failed to remove.'); throw e; }
  }, []);

  const pickAttributes = useCallback((ids: string[]): AttributeMap => {
    const out: AttributeMap = {};
    for (const id of ids) {
      const v = attributes[id];
      const empty = v === undefined || v === null || v === '';
      if (!empty) out[id] = v;
    }
    return out;
  }, [attributes]);

  const addPasskey = useCallback(async () => {
    if (!dekRef.current) throw new Error('Unlock the vault before adding a passkey.');
    if (!session?.user) throw new Error('You must be signed in.');
    setError(null);
    try {
      const { credentialId, prfOutput } = await registerPasskeyWithPrf({
        userId: session.user.id,
        userName: session.user.email || 'user',
        displayName: session.user.user_metadata?.full_name || session.user.email || 'Tinaney user',
      });
      const kek = await deriveKeyFromPrf(prfOutput);
      const envelopePasskey = await wrapDEK(dekRef.current, kek);
      const rec = await persistPasskeyEnvelope(envelopePasskey, bytesToB64url(credentialId));
      setRecord(rec);
    } catch (e: any) { setError(e?.message || 'Failed to add a passkey.'); throw e; }
  }, [session]);

  const removePasskey = useCallback(async () => {
    setError(null);
    if (!record?.envelopePin) throw new Error('Cannot remove the only unlock method.');
    try { const rec = await removePasskeyEnvelope(); setRecord(rec); }
    catch (e: any) { setError(e?.message || 'Failed to remove the passkey.'); throw e; }
  }, [record]);

  const changePin = useCallback(async (newPin: string) => {
    if (!dekRef.current) throw new Error('Unlock the vault before changing the PIN.');
    setError(null);
    try {
      const salt = randomBytes(SALT_BYTES);
      const kek = await deriveKeyFromPin(newPin, salt);
      const envelopePin = await wrapDEK(dekRef.current, kek);
      const rec = await persistPinEnvelope(envelopePin, bytesToB64(salt));
      setRecord(rec);
    } catch (e: any) { setError(e?.message || 'Failed to change the PIN.'); throw e; }
  }, []);

  const value: VaultContextValue = {
    status, capability, definitions, attributes,
    hasPin: !!record?.envelopePin,
    hasPasskey: !!record?.envelopePasskey,
    error,
    setupWithPin, unlockWithPin, unlockWithPasskey,
    saveAttribute, removeAttribute, pickAttributes,
    addPasskey, removePasskey, changePin,
    lock, refresh: loadRecord, clearError,
  };

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
};
